import { useEffect, useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { ApiError } from '@/lib/api';
import { toast } from '@/lib/toast';
import { useCreateCluster } from '../hooks/useStructureQueries';

interface ClusterDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  companyId: string;
}

/** Простое создание кластера — имя + короткое имя. Группировка хозяйств. */
export function ClusterDialog({ open, onOpenChange, companyId }: ClusterDialogProps) {
  const create = useCreateCluster();
  const [name, setName] = useState('');
  const [shortName, setShortName] = useState('');
  const [error, setError] = useState<string | undefined>();

  useEffect(() => {
    if (open) {
      setName('');
      setShortName('');
      setError(undefined);
    }
  }, [open]);

  const submit = async () => {
    if (!name.trim()) {
      setError('Укажите название кластера');
      return;
    }
    try {
      await create.mutateAsync({
        company_id: companyId,
        name: name.trim(),
        short_name: shortName.trim() || null,
      });
      toast.success(`Кластер «${name.trim()}» создан`);
      onOpenChange(false);
    } catch (e) {
      if (e instanceof ApiError) setError(e.detail);
      else setError('Не удалось создать кластер');
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Новый кластер</DialogTitle>
          <DialogDescription>
            Кластер группирует хозяйства — удобно, когда их много. Хозяйства
            привяжете к нему позже.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="space-y-1.5">
            <Label htmlFor="cluster-name">
              Название <span className="text-destructive">*</span>
            </Label>
            <Input
              id="cluster-name"
              placeholder="напр. «Север»"
              value={name}
              onChange={(e) => {
                setName(e.target.value);
                setError(undefined);
              }}
              aria-invalid={!!error}
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="cluster-short">Короткое имя</Label>
            <Input
              id="cluster-short"
              placeholder="для списков"
              value={shortName}
              onChange={(e) => setShortName(e.target.value)}
            />
          </div>
          {error && <p className="text-sm text-destructive">{error}</p>}
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={create.isPending}
          >
            Отмена
          </Button>
          <Button
            onClick={submit}
            disabled={create.isPending}
          >
            {create.isPending ? 'Создаём…' : 'Создать кластер'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
